/* eslint-disable no-console */
import {
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from "@angular/router";

import { isPlatformBrowser } from "@angular/common";
import { Meta } from "@angular/platform-browser";
import { environment } from "@env";
import { FingerprintjsProAngularService } from "@fingerprintjs/fingerprintjs-pro-angular";
import { TranslocoService } from "@ngneat/transloco";
import {
  AnalyticsService,
  AuthService,
  IntercomService,
  LocalStorageService,
  TranslateService,
} from "@services";
import { Store, getUser, getUserTokens } from "@store";
import { filter, from, map, mergeMap, switchMap } from "rxjs";
import { CanonicalService } from "./core/service/canonical.service";
import { ElfsightService } from "./core/service/elfsight.service";
import { RequestHeaderService } from "./core/service/request-header.service";
import { TitleService } from "./core/service/title.service";
import { GtmV2Service } from "./core/service/gtmV2.service";
import { GtmTagPageInfo } from "./shared/model/gtmV2.model";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
  @ViewChild("canvas", { static: true }) public canvasRef!: ElementRef<HTMLCanvasElement>;
  public previousUrl: string | null = null;
  public currentUrl: string | null = null;

  constructor(
    private _intercomService: IntercomService,
    private _router: Router,
    private _store: Store,
    private _analyticsService: AnalyticsService,
    private _translateService: TranslateService,
    private _authService: AuthService,
    private _localStorageService: LocalStorageService,
    private _elfsightService: ElfsightService,
    private _canonicalService: CanonicalService,
    private _translocoService: TranslocoService,
    private _activatedRoute: ActivatedRoute,
    private _titleService: TitleService,
    private _fingerpringService: FingerprintjsProAngularService,
    private _requestHeaderService: RequestHeaderService,
    private _gtmV2Service: GtmV2Service,
    private _meta: Meta,
    @Inject(PLATFORM_ID) private _platformId: object,
    private _destroyRef: DestroyRef,
  ) {
    const isDev = !environment.production;

    if (isPlatformBrowser(this._platformId) && isDev) {
      this._meta.updateTag({
        name: "robots",
        content: "noindex, nofollow",
      });
    }
  }

  public ngOnInit(): void {
    if (environment.production) {
      from(this._fingerpringService.getVisitorData()).subscribe({
        next: (data) => {
          this._requestHeaderService.setRequestHeaderValue(data.visitorId);
        },
        error: (err) => console.error(err),
      });
    }

    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => {
        if (environment.production) {
          this._elfsightService.addElfsightWidget();
        }

        const activeLang = this._translocoService.getActiveLang();
        this._canonicalService.setCanonicalAndHreflangs(activeLang);
      });

    this._authService
      .isAuthenticated()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this._store.dispatch(getUser());
          this._store.dispatch(getUserTokens());
          this._analyticsService.deleteActiveCampaignScript();
        } else {
          this._analyticsService.injectActiveCampaignScript();
          // This is purely for backwards compatibility, since tokens are not stored in local storage anymore
          this._localStorageService.remove("accessToken");
          this._localStorageService.remove("refreshToken");
        }
      });

    this._intercomService.boot();
    const externalReferrer = document.referrer || null;

    this.currentUrl = window.location.href;

    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        switchMap((event) => {
          this.previousUrl = this.currentUrl;
          const langPrefix = this._translateService.getSelectedLanguage() === "es" ? "/es" : "";
          this.currentUrl = window.location.origin + langPrefix + event.urlAfterRedirects;

          const pageObjForGtm: Partial<GtmTagPageInfo> = {
            url: this.currentUrl,
          };

          const pageReferrer =
            this.previousUrl && this.previousUrl !== this.currentUrl
              ? this.previousUrl
              : externalReferrer;

          if (typeof document !== "undefined" && typeof window !== "undefined") {
            pageObjForGtm.title = document.title || "Unknown Title";
            pageObjForGtm.referrer = pageReferrer || "No Referrer";
            pageObjForGtm.location = window.location.href || "Unknown Location";
          }

          this._gtmV2Service.pushPageViewGtmTag(pageObjForGtm);

          return this._authService.isAuthenticated();
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this._store.dispatch(getUserTokens());
        }
      });

    // Disable right - click
    //document.addEventListener("contextmenu", (e) => e.preventDefault());

    function ctrlShiftKey(e: KeyboardEvent, keyCode: string) {
      return e.ctrlKey && e.shiftKey && e.keyCode === keyCode.charCodeAt(0);
    }

    document.onkeydown = (e: KeyboardEvent) => {
      // Disable F12, Ctrl + Shift + I, Ctrl + Shift + J, Ctrl + U
      if (
        e.keyCode === 123 ||
        ctrlShiftKey(e, "I") ||
        ctrlShiftKey(e, "J") ||
        ctrlShiftKey(e, "C") ||
        (e.ctrlKey && e.keyCode === "U".charCodeAt(0))
      ) {
        return environment.isInspect;
      }

      return true;
    };
  }

  public setPageTitles(): void {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route = this._activatedRoute;
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        mergeMap((route) => route.data),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((data) => {
        if (data["title"]) {
          const translatedTitle = this._translocoService.translate(data["title"]);
          this._titleService.setTitle(translatedTitle);
        }
      });
  }
}
